import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { matchPlatform } from '@/lib/hooks/utils/match-platform';
import { getGoogleTrendDataApi } from '@/lib/service/erasa';
import { page3JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';

const HomeContent = dynamic(() => import('@/components/[platform]-vs-[platform]'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  const localeRes = await serverSideTranslations(locale, ['common']);

  const parentPath = removeLastPathSegment(resolvedUrl);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  // const articleList = await getArticleRecommendList(null, locale);
  const regex = /\/([a-zA-Z0-9-]+)-vs-([a-zA-Z0-9-]+)/;

  const matches = resolvedUrl.match(regex);

  if (matches) {
    const platform1 = matches[1];
    const platform2 = matches[2];
    let currentURL = `https://${req.headers.host}/compare/${platform1}-vs-${platform2}`;
    if (locale != 'en') {
      currentURL = `https://${req.headers.host}/${locale}/compare/${platform1}-vs-${platform2}`;
    }
    const [platList1, platList2, sixPlatList] = await matchPlatform(platform1, platform2);
    const { data: googleTrendData1 } = await getGoogleTrendDataApi(platform1, locale);
    const { data: googleTrendData2 } = await getGoogleTrendDataApi(platform2, locale);

    const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

    const parentUrl = siteUrl + parentPath;

    const parentTitle = hyphenatedToTitleCase(parentPath);

    const pageTitle = `${tempData?.db_c_title?.replace('xxx1', platform1)?.replace('xxx2', platform2)}`;

    const pageDescription = `${tempData?.db_c_desc?.replace('xxx1', platform1)?.replace('xxx2', platform2)}`;

    const pageKeywords = tempData?.head_Meta_Keys ?? null;

    const jsonLd = page3JsonLd(
      {
        title: pageTitle,
        desc: pageDescription,
        url: currentURL,
      },
      {
        title: parentTitle,
        url: parentUrl,
      },
      {
        url: siteUrl,
      },
    );
    return {
      props: {
        ...localeRes,
        herf: currentURL,
        locale,
        platList1,
        platList2,
        sixPlatList,
        googleTrendData1: googleTrendData1 ?? [],
        googleTrendData2: googleTrendData2 ?? [],
        title: pageTitle,
        description: pageDescription,
        keywords: pageKeywords,
        jsonLd: jsonLd,
        slug: `${platform1}-vs-${platform2}`,
      },
    };
  }
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props: any) => {
  return <HomeContent {...props} />;
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;
  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />
          {/* <meta name="viewport" content="width=device-width, initial-scale=1" /> */}

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="preload" as="image" href="https://res-front.pumpsoul.com/prod-erasa/image/competion.webp" />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/compare/${page.props.slug}`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/compare/${page.props.slug}`} />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_APP_WEB_HOST}/compare/${page.props.slug}`}
          />

          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
